import type { PricingPlanConfig } from "@/store/configStore";
import React from "react";
import BenefitItem from "./BenefitItem";

interface PlanBenefitsSectionProps {
  /**
   * The plan configuration data containing benefits
   */
  planConfig: PricingPlanConfig | undefined;
}

/**
 * Dynamically renders benefit sections for a plan based on config from the server
 * Falls back to empty state if config is not available
 */
export const PlanBenefitsSection: React.FC<PlanBenefitsSectionProps> = ({
  planConfig,
}) => {
  // If no benefits config is available yet, return null or loading state
  if (!planConfig) {
    return null;
  }

  const { benifits } = planConfig;

  if (!benifits) {
    return null;
  }

  const { basic = [], unlimitedAccess = [], unlockModels = [] } = benifits;

  return (
    <div className="mt-6 text-text-tertiary text-xs font-normal">
      {/* Basic Benefits Section */}
      {basic.length > 0 && (
        <ul className="flex flex-col gap-3">
          {benifits.basic.map((item, index) => (
            <li key={`basic-${index}`}>
              <BenefitItem
                label={item.label}
                active={item.isActive}
                is365Unlimited={item.is365Ulimited}
                desc={item.desc}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Unlimited Access Section */}
      {unlimitedAccess.length > 0 && (
        <>
          <div className="h-px my-6 bg-white/[.12]"></div>

          <h3 className="text-[#A7B0FF] text-base font-bold">
            UNLIMITED ACCESS
          </h3>

          <ul className="flex flex-col gap-2 mt-3">
            {unlimitedAccess.map((item, index) => (
              <li key={`unlimited-${index}`}>
                <BenefitItem
                  label={item.label}
                  active={item.isActive}
                  is365Unlimited={item.is365Ulimited}
                  desc={item.desc}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Unlock Models Section */}
      {unlockModels.length > 0 && (
        <>
          <div className="h-px my-6 bg-white/[.12]"></div>

          <h3 className="text-text-primary text-base font-bold">
            UNLOCK MODELS
          </h3>

          <ul className="flex flex-col gap-2 mt-3">
            {unlockModels.map((item, index) => (
              <li key={`unlock-${index}`}>
                <BenefitItem
                  label={item.label}
                  active={item.isActive}
                  is365Unlimited={item.is365Ulimited}
                  desc={item.desc}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

PlanBenefitsSection.displayName = "PlanBenefitsSection";
